import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  TenantBranding,
  TenantSettings,
  fetchTenantSettings,
  saveTenantBranding,
} from '../../api/tenantAdmin';
import { readableOn } from '../../utils/contrast';
import './TenantSettingsPage.css';

/**
 * Where a partner sets their own skin.
 *
 * Only presentation is editable. Which hostnames serve them, whether they
 * are active and what we bill are shown read-only where they are shown at
 * all, because those are the contract rather than the design. The backend
 * enforces that too; this screen just avoids offering a control that would
 * be refused.
 */

type Field = {
  key: keyof TenantBranding;
  label: string;
  hint?: string;
  type?: 'text' | 'colour' | 'url' | 'email' | 'font';
};

const FIELDS: Field[] = [
  { key: 'productName', label: 'Product name', hint: 'What your customers call the service.' },
  { key: 'legalName', label: 'Legal name', hint: 'Used in contracts and documents.' },
  { key: 'supportEmail', label: 'Support email', type: 'email', hint: 'Where your customers write for help.' },
  { key: 'websiteUrl', label: 'Website', type: 'url' },
  { key: 'logoUrl', label: 'Logo URL', type: 'url', hint: 'Must be https. Shown instead of your product name.' },
  { key: 'faviconUrl', label: 'Favicon URL', type: 'url' },
  {
    key: 'heroImageUrl',
    label: 'Dashboard banner',
    type: 'url',
    // The numbers are the real ones: the box is 200px tall and as wide as
    // the content area, and the image is object-fit: cover. Telling people
    // "any size" produces banners with the subject cropped out of frame.
    hint:
      '2400 x 600 px, https. Only a wide strip through the middle is shown, ' +
      'so keep the subject centred. Larger images are cropped, not shrunk, ' +
      'so keep the file small: your customers download it.',
  },
  { key: 'backgroundColor', label: 'Page background', type: 'colour', hint: 'Behind everything. Leave blank for white.' },
  { key: 'surfaceColor', label: 'Card background', type: 'colour', hint: 'Panels sitting on the page.' },
  { key: 'primaryColor', label: 'Primary colour', type: 'colour' },
  { key: 'primaryStrongColor', label: 'Primary, darker', type: 'colour', hint: 'Hover and active states.' },
  { key: 'accentColor', label: 'Accent colour', type: 'colour' },
  // The new customer app. Each is optional: left blank, the app keeps its own.
  {
    key: 'logoInverseUrl',
    label: 'Logo on colour',
    type: 'url',
    hint: 'A light version of your logo, https, for the coloured bands (sign-in, account header). Blank: your product name is shown there.',
  },
  { key: 'inkColor', label: 'Text colour', type: 'colour', hint: 'Body text and lines. Keep it dark enough to read on your page background.' },
  { key: 'displayFont', label: 'Heading font', type: 'font', hint: 'Headings and buttons.' },
  { key: 'textFont', label: 'Body font', type: 'font', hint: 'Everything else.' },
];

/** Google Fonts for the chosen faces, so the preview shows them. */
const fontsHref = (fonts: (string | null | undefined)[]) => {
  const families = fonts.filter((f, i, all): f is string => !!f && all.indexOf(f) === i);
  if (!families.length) return null;
  return `https://fonts.googleapis.com/css2?${families
    .map(f => `family=${f.replace(/ /g, '+')}:wght@400;700;800`)
    .join('&')}&display=swap`;
};

/**
 * The languages the customer app actually has bundles for.
 *
 * A partner can narrow this, never extend it: asking for a language we do
 * not ship would offer their customer a page of untranslated keys. The app
 * intersects whatever is stored with what it has, so an extra code here
 * would be ignored rather than dangerous, but listing one we lack would
 * promise something in this form that never arrives.
 */
const AVAILABLE_LOCALES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'uk', label: 'Українська' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'zh', label: '简体中文' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'it', label: 'Italiano' },
  // Only on a partner's site, and only when ticked here: unticking everything
  // offers every language above but this one (coepi-bold lib/i18n.ts).
  { code: 'ru', label: 'Русский' },
];

const EMPTY: TenantBranding = {
  productName: null,
  legalName: null,
  supportEmail: null,
  websiteUrl: null,
  primaryColor: null,
  primaryStrongColor: null,
  accentColor: null,
  backgroundColor: null,
  surfaceColor: null,
  logoUrl: null,
  faviconUrl: null,
  heroImageUrl: null,
  logoInverseUrl: null,
  inkColor: null,
  displayFont: null,
  textFont: null,
  locales: null,
};

const TenantSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [draft, setDraft] = useState<TenantBranding>(EMPTY);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const data = await fetchTenantSettings(token);
        if (cancelled) return;
        setSettings(data);
        setDraft({ ...EMPTY, ...(data?.branding || {}) });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load your settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Load the chosen faces for the preview only; the customer app loads its own.
  const previewFonts = fontsHref([draft.displayFont, draft.textFont]);
  useEffect(() => {
    if (!previewFonts) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = previewFonts;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [previewFonts]);

  const set = (key: keyof TenantBranding, value: string) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  const save = useCallback(async () => {
    if (!currentUser) return;
    setSaving(true);
    setError('');
    setSaved('');
    try {
      const token = await currentUser.getIdToken();
      // Send only what changed, so a field somebody else edited in the
      // meantime is not overwritten with a stale value from this form.
      const changes: Partial<TenantBranding> = {};
      const original = settings?.branding || EMPTY;
      (Object.keys(draft) as (keyof TenantBranding)[]).forEach(key => {
        if (key === 'locales') return;
        const next = (draft[key] ?? '') as string;
        const before = (original[key] ?? '') as string;
        if (next !== before) changes[key] = (next === '' ? null : next) as any;
      });
      // An array, so compare by content rather than identity. Order is ours,
      // not theirs, so a stable join is a fair comparison.
      const nextLocales = [...(draft.locales ?? [])].sort().join(',');
      const beforeLocales = [...(original.locales ?? [])].sort().join(',');
      if (nextLocales !== beforeLocales) changes.locales = draft.locales ?? [];
      if (Object.keys(changes).length === 0) {
        setSaved('Nothing to save.');
        return;
      }
      await saveTenantBranding(token, changes);
      setSettings(prev => (prev ? { ...prev, branding: { ...prev.branding, ...changes } } : prev));
      setSaved('Saved. Your customers will see this on the next page load.');
    } catch (e: any) {
      setError(e?.message || 'Could not save those changes.');
    } finally {
      setSaving(false);
    }
  }, [currentUser, draft, settings]);

  if (loading) return <div className="tenant-settings"><p>Loading…</p></div>;

  return (
    <div className="tenant-settings">
      <h2>Your branding</h2>
      <p className="tenant-settings-intro">
        This is what your customers see at{' '}
        {settings?.hosts?.length ? settings.hosts.join(', ') : 'your address'}. They never
        see Coepi.
      </p>

      {error && <div className="tenant-settings-error" role="alert">{error}</div>}
      {saved && <div className="tenant-settings-saved" role="status">{saved}</div>}

      <div className="tenant-settings-grid">
        <form
          className="tenant-settings-form"
          onSubmit={e => {
            e.preventDefault();
            save();
          }}
        >
          {FIELDS.map(field => (
            <label key={field.key} className="tenant-field">
              <span className="tenant-field-label">{field.label}</span>
              <span className="tenant-field-control">
                {field.type === 'colour' && (
                  <input
                    type="color"
                    aria-label={`${field.label} swatch`}
                    value={(draft[field.key] as string) || '#1f2937'}
                    onChange={e => set(field.key, e.target.value)}
                  />
                )}
                {field.type === 'font' ? (
                  <select value={(draft[field.key] as string) ?? ''} onChange={e => set(field.key, e.target.value)}>
                    <option value="">The app's own</option>
                    {(settings?.fonts ?? []).map(font => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={(draft[field.key] as string) ?? ''}
                    placeholder={field.type === 'colour' ? '#5B2E91' : ''}
                    onChange={e => set(field.key, e.target.value)}
                  />
                )}
              </span>
              {field.hint && <span className="tenant-field-hint">{field.hint}</span>}
            </label>
          ))}

          <fieldset className="tenant-locales">
            <legend>Languages your customers can choose</legend>
            <p className="tenant-field-hint">
              Leave all unticked to offer every language we support except Russian,
              which your customers get only when you tick it.
            </p>
            {AVAILABLE_LOCALES.map(language => {
              const chosen = draft.locales ?? [];
              const on = chosen.includes(language.code);
              return (
                <label key={language.code} className="tenant-locale">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setDraft(prev => {
                        const current = prev.locales ?? [];
                        return {
                          ...prev,
                          locales: on
                            ? current.filter(c => c !== language.code)
                            : [...current, language.code],
                        };
                      })
                    }
                  />
                  <span>{language.label}</span>
                </label>
              );
            })}
          </fieldset>

          <button type="submit" className="tenant-settings-save" disabled={saving}>
            {saving ? 'Saving…' : 'Save branding'}
          </button>
        </form>

        {/* Shows the combination rather than nine separate swatches: the
            thing that goes wrong is a primary and an accent that clash, and
            you cannot see that in a colour picker. */}
        <aside
          className="tenant-preview"
          style={
            {
              '--preview-primary': draft.primaryColor || '#1f2937',
              '--preview-strong': draft.primaryStrongColor || draft.primaryColor || '#111827',
              '--preview-accent': draft.accentColor || '#6366f1',
              '--preview-background': draft.backgroundColor || '#fdfdfc',
              '--preview-surface': draft.surfaceColor || '#ffffff',
              // Same rule the customer app uses, so the preview shows what
              // they will actually get rather than white on everything.
              '--preview-on-primary': readableOn(draft.primaryColor),
              '--preview-on-accent': readableOn(draft.accentColor),
            } as React.CSSProperties
          }
        >
          <h3>Preview</h3>
          <div
            className="tenant-preview-card"
            style={{
              ...(draft.inkColor ? { color: draft.inkColor } : {}),
              ...(draft.textFont ? { fontFamily: `'${draft.textFont}', sans-serif` } : {}),
            }}
          >
            {draft.heroImageUrl && (
              <div className="tenant-preview-hero">
                <img src={draft.heroImageUrl} alt="" />
              </div>
            )}
            <div className="tenant-preview-brand">
              {draft.logoUrl ? (
                <img src={draft.logoUrl} alt={draft.productName || 'Logo'} />
              ) : (
                <span>{draft.productName || 'Your product name'}</span>
              )}
              <small>Powered by Coepi</small>
            </div>
            <button
              type="button"
              className="tenant-preview-primary"
              style={draft.displayFont ? { fontFamily: `'${draft.displayFont}', sans-serif` } : undefined}
            >
              {draft.logoInverseUrl ? (
                <img src={draft.logoInverseUrl} alt="" className="tenant-preview-inverse" />
              ) : null}
              Get your NIF
            </button>
            <button type="button" className="tenant-preview-accent">Secondary action</button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TenantSettingsPage;
